import { ScrollArea as BaseScrollArea } from "@base-ui/react/scroll-area";
import type React from "react";
import { type CSSProperties, forwardRef, useCallback, useRef } from "react";
import {
  scrollAreaRootClass,
  scrollAreaScrollbarClass,
  scrollAreaThumbClass,
  scrollAreaViewportClass,
  scrollAreaViewportDraggableClass,
} from "./styles.css";

interface ScrollAreaProps {
  children: React.ReactNode;
  className?: string;
  style?: CSSProperties;
  maxHeight?: string | number;
  draggable?: boolean;
}

export const ScrollArea = forwardRef<HTMLDivElement, ScrollAreaProps>(
  function ScrollArea(
    { children, className, style, maxHeight, draggable = false },
    ref,
  ) {
    const viewportRef = useRef<HTMLDivElement | null>(null);
    const dragState = useRef({
      active: false,
      startX: 0,
      startY: 0,
      scrollLeft: 0,
      scrollTop: 0,
      moved: false,
    });

    const onPointerDown = useCallback(
      (e: React.PointerEvent<HTMLDivElement>) => {
        if (!draggable) return;
        if (e.pointerType === "touch") return;
        const el = viewportRef.current;
        if (!el) return;
        dragState.current = {
          active: true,
          startX: e.clientX,
          startY: e.clientY,
          scrollLeft: el.scrollLeft,
          scrollTop: el.scrollTop,
          moved: false,
        };
        el.setPointerCapture(e.pointerId);
      },
      [draggable],
    );

    const onPointerMove = useCallback(
      (e: React.PointerEvent<HTMLDivElement>) => {
        const s = dragState.current;
        if (!s.active) return;
        const el = viewportRef.current;
        if (!el) return;
        const dx = e.clientX - s.startX;
        const dy = e.clientY - s.startY;
        if (!s.moved && Math.hypot(dx, dy) > 3) s.moved = true;
        el.scrollLeft = s.scrollLeft - dx;
        el.scrollTop = s.scrollTop - dy;
      },
      [],
    );

    const onPointerUp = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
      const s = dragState.current;
      if (!s.active) return;
      s.active = false;
      viewportRef.current?.releasePointerCapture(e.pointerId);
      if (s.moved) {
        // Swallow the click that follows so drag doesn't trigger buttons/links underneath
        const stopClick = (ev: MouseEvent) => {
          ev.stopPropagation();
          ev.preventDefault();
        };
        const el = viewportRef.current;
        el?.addEventListener("click", stopClick, { capture: true, once: true });
        setTimeout(() => el?.removeEventListener("click", stopClick, true), 0);
      }
    }, []);

    return (
      <BaseScrollArea.Root
        ref={ref}
        className={`${scrollAreaRootClass}${className ? ` ${className}` : ""}`}
        style={{
          maxHeight:
            typeof maxHeight === "number" ? `${maxHeight}px` : maxHeight,
          ...style,
        }}
      >
        <BaseScrollArea.Viewport
          ref={viewportRef}
          className={`${scrollAreaViewportClass}${draggable ? ` ${scrollAreaViewportDraggableClass}` : ""}`}
          onPointerDown={draggable ? onPointerDown : undefined}
          onPointerMove={draggable ? onPointerMove : undefined}
          onPointerUp={draggable ? onPointerUp : undefined}
          onPointerCancel={draggable ? onPointerUp : undefined}
        >
          {children}
        </BaseScrollArea.Viewport>
        <BaseScrollArea.Scrollbar
          orientation="vertical"
          className={scrollAreaScrollbarClass}
        >
          <BaseScrollArea.Thumb className={scrollAreaThumbClass} />
        </BaseScrollArea.Scrollbar>
        <BaseScrollArea.Scrollbar
          orientation="horizontal"
          className={scrollAreaScrollbarClass}
        >
          <BaseScrollArea.Thumb className={scrollAreaThumbClass} />
        </BaseScrollArea.Scrollbar>
      </BaseScrollArea.Root>
    );
  },
);

ScrollArea.displayName = "ScrollArea";
